import { Box, Container, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

interface PageLayoutProps {
    children: React.ReactNode;
    title: string;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    actions?: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children, title, maxWidth = 'xl', actions }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Container
            maxWidth={maxWidth}
            disableGutters
            sx={{
                width: '100%',
                height: '100%',
                px: { xs: 0, sm: 2, md: 3 },
                backgroundColor: 'transparent', // Use transparent to inherit from parent
                margin: 0,
                maxWidth: '100%',
                pt: { xs: '85px', sm: 0 }, // Add top padding on mobile to account for the header
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    boxShadow: 'none',
                    overflow: 'hidden',
                    bgcolor: 'transparent', // Use transparent to inherit from parent
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1.5, sm: 2.5 },
                        width: '100%',
                        py: 0,
                        my: 0,
                    }}
                >
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        sx={
                            isTablet
                                ? {
                                      fontSize: {
                                          xs: '1.25rem',
                                          sm: '1.5rem',
                                          md: '1.75rem',
                                      },
                                      fontWeight: 500,
                                      lineHeight: 1.4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: {
                                          xs: '100%',
                                          sm: '60%',
                                      },
                                      color: (theme) =>
                                          theme.palette.mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.8)',
                                      letterSpacing: '-0.01em',
                                  }
                                : {
                                      fontSize: {
                                          xs: '1.25rem',
                                          sm: '1.5rem',
                                          md: '1.75rem',
                                      },
                                      fontWeight: 450,
                                      color: (theme) =>
                                          theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                      mb: 0,
                                      pb: '22px',
                                      mt: { xs: '16px', sm: '8px' }, // Increased top margin for mobile view
                                  }
                        }
                    >
                        {title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        {actions}
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        py: 0,
                        mt: '2px',
                        pb: '16px',
                    }}
                >
                    {children}
                </Box>
            </Paper>
        </Container>
    );
};

export default PageLayout;
